import { z } from 'zod';

const envSchema = z.object({
  MODE: z.enum(['development', 'test', 'production']),
  // Add VITE_* public variables here as they are needed.
  // NEVER add secrets or non-VITE_-prefixed variables.

  // Base URL for the backend API. Defaults to '/' for local dev (Vite proxy handles routing).
  // Set to the full API origin (e.g. https://api.getkiku.com) when frontend and backend are on different domains.
  VITE_API_BASE_URL: z.string().optional().default('/'),

  VITE_PUBLIC_POSTHOG_KEY: z.string().min(1, 'VITE_PUBLIC_POSTHOG_KEY is required'),
  VITE_PUBLIC_POSTHOG_HOST: z.string().url().optional().default('https://eu.i.posthog.com'),
  VITE_PUBLIC_INTERCOM_APP_ID: z.string().min(1, 'VITE_PUBLIC_INTERCOM_APP_ID is required'),

  // Sentry
  VITE_SENTRY_DSN: z.string().url('VITE_SENTRY_DSN must be a valid URL'),
  VITE_SENTRY_ENVIRONMENT: z.string().optional(),
  VITE_SENTRY_RELEASE: z.string().optional()
});

export type FrontendConfig = z.infer<typeof envSchema>;

function parseFrontendEnv(): FrontendConfig {
  const result = envSchema.safeParse({
    MODE: import.meta.env.MODE,
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_PUBLIC_POSTHOG_KEY: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
    VITE_PUBLIC_POSTHOG_HOST: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    VITE_PUBLIC_INTERCOM_APP_ID: import.meta.env.VITE_PUBLIC_INTERCOM_APP_ID,
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    VITE_SENTRY_RELEASE: import.meta.env.VITE_SENTRY_RELEASE
  });
  if (!result.success) {
    const issues = result.error.issues.map((i) => `  ${i.path.join('.')}: ${i.message}`).join('\n');
    throw new Error(`Frontend environment validation failed:\n${issues}`);
  }
  return result.data;
}

export const frontendConfig: FrontendConfig = parseFrontendEnv();
